import React, { ComponentProps, useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import { refetchReport } from '../hooks/use-report-spec';
import { refetchReports } from '../hooks/use-reports';
import useSaveReport, { useUpdateReport } from '../hooks/use-save-report';
import ReportContextProvider, {
  useReportContext,
} from '../context/ReportContext';
import EditIcon from '@mui/icons-material/Edit';
import { Report } from '../types';
import { useTranslation } from 'utils/translation';
import FluentForm from '../../../components/v2/Form/FluentForm';
import { text } from '../../../components/v2/Form/factory';
import Section from 'components/v2/Section/Section';
import Form from '../../../components/v2/Form/Form';
import { FieldType } from '../../../components/v2/Form/types';
import Modal from '../../../components/v2/Modal/Modal';
import Spacer from '../../../components/v2/Spacer/Spacer';
import { useFormContext } from 'react-hook-form';
import Info from '../../../components/v2/Info/Info';
import { EmployeeRole } from '../../auth';

type Values = {
  name: string;
  isShared: boolean;
  shouldOverrideAttributes: boolean;
  assignGroups: boolean;
  groups?: { label: string; value: string }[];
};

type Props = {
  onSave(values: Values): Promise<void>;
};

const ReportShareOptions = () => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const groupsEnabled = watch('assignGroups');
  const isShared = watch('isShared');
  const groups = watch('groups');

  useEffect(() => {
    if (groupsEnabled) {
      setValue('isShared', false);
    }
  }, [groupsEnabled]);

  let message = null;

  if (!isShared && !groupsEnabled) {
    message = t('report.save.visible_only_to_you');
  }

  if (isShared) {
    message = t('report.save.visible_to_all_users');
  }

  if (groupsEnabled && groups.length === 0) {
    message = t('report.save.select_at_least_one_group');
  }

  if (groupsEnabled && groups.length > 0) {
    message = t('report.save.visible_for_groups', {
      groups: groups.map((item: any) => item.label).join(', '),
    });
  }

  return (
    <>
      <Form.Field
        name="isShared"
        type={FieldType.Checkbox}
        label={t('report.save.share_with_other_users')}
        disabled={groupsEnabled}
      />
      <EmployeeRole>
        <Form.Field
          name="assignGroups"
          type={FieldType.Checkbox}
          label={t('report.save.share_with_groups')}
        />
        {groupsEnabled && (
          <Form.Field
            name="groups"
            type={FieldType.ServerAutocomplete}
            optionsUrl="/api/group"
            multiple
            rules={{ required: groupsEnabled }}
          />
        )}
        <Spacer size={4} />
        {message && <Info message={message} />}
      </EmployeeRole>
    </>
  );
};

const SaveReportForm = ({ onSave }: Props) => {
  const { t } = useTranslation();
  const {
    name: defaultName,
    isEditable,
    isShared,
    groups,
  } = useReportContext();

  return (
    <Section padding="small">
      <FluentForm<Values>
        action={onSave}
        defaultValues={{
          name: isEditable ? defaultName : '',
          isShared,
          shouldOverrideAttributes: true,
          assignGroups: isEditable ? groups?.length > 0 : false,
          groups: isEditable
            ? groups?.map((item) => ({
                label: item.name,
                value: item.id,
              }))
            : [],
        }}
        fields={[
          text({
            name: 'name',
            label: t('Name'),
            rules: { required: true },
            autoFocus: true,
          }),
        ]}
      >
        <ReportShareOptions />
        <Spacer size={4} />
      </FluentForm>
    </Section>
  );
};

const SaveReport = ({
  onClose,
  filters,
  active = [],
  attributes = [],
  reportId,
  hook: useSaveMutation = useSaveReport,
}: any) => {
  const mutation = useSaveMutation();

  const handleSave = async ({
    name,
    isShared,
    shouldOverrideAttributes,
    groups,
    assignGroups,
  }: Values) => {
    await mutation.mutateAsync({
      id: reportId,
      name,
      filters,
      active: Array.from(active).map((item: any) => item.name),
      attributes,
      isShared,
      shouldOverrideAttributes,
      groups: assignGroups ? (groups || []).map((item) => item.value) : [],
    });
    void refetchReports();
    void refetchReport({ id: reportId });
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <Modal.Content>
        <Section>
          <SaveReportForm onSave={handleSave} />
        </Section>
      </Modal.Content>
    </Modal>
  );
};

export const UpdateReportName = (props: {
  reportId: Report['id'];
  onClose(): void;
}) => {
  return <SaveReport {...props} hook={useUpdateReport} />;
};

const DefaultButton: React.FC<ComponentProps<typeof IconButton>> = (props) => (
  <IconButton size="small" {...props}>
    <EditIcon fontSize="inherit" />
  </IconButton>
);

export const UpdateReportNameButton: React.VFC<{
  report: Report;
  component?: React.ComponentType;
}> = ({ component: Component = DefaultButton, report }) => {
  const [active, setActive] = useState(false);

  return (
    <ReportContextProvider {...report}>
      <>
        <Component onClick={() => setActive(true)} />
        {active && (
          <UpdateReportName
            reportId={report.id}
            onClose={() => setActive(false)}
          />
        )}
      </>
    </ReportContextProvider>
  );
};

export default SaveReport;
